import React, { useState, useEffect, useRef } from 'react';
import { Monitor, Sparkles, CheckCircle, Target } from 'lucide-react';
import { portfolioData } from '../data/mock';

const timelineIcons = {
  monitor: Monitor,
  sparkles: Sparkles
};

const Timeline = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="timeline" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <span>Career Timeline</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Evolution of an <span className="gradient-text">Engineer</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Growth of responsibilities within the same organization — from systems to AI</p>
          </div>

          <div className="relative">
            <div className="absolute left-8 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-500/50 via-purple-500/30 to-transparent -translate-x-px sm:translate-x-0" />

            {portfolioData.timeline.map((item, i) => {
              const Icon = timelineIcons[item.icon] || Monitor;
              const isLeft = i % 2 === 0;
              return (
                <div key={item.period} className="relative mb-12 last:mb-0"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${i * 200}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.6s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className={`flex flex-col sm:flex-row items-start ${isLeft ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>

                    <div className={`hidden sm:block w-1/2 ${isLeft ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                      <div className={`glass rounded-2xl p-6 sm:p-8 ${item.current ? 'border-indigo-500/20 bg-indigo-500/5' : 'hover:border-white/10'}`}>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-mono text-indigo-400">{item.period}</span>
                          {item.current && (
                            <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-mono">Current</span>
                          )}
                        </div>
                        <h3 className={`text-lg font-bold mb-3 ${item.current ? 'text-white' : 'text-white'}`}>{item.role}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed mb-4">{item.description}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {item.highlights.map((h) => (
                            <div key={h} className="flex items-center space-x-1.5">
                              <CheckCircle size={12} className="text-indigo-400 flex-shrink-0" />
                              <span className="text-gray-500 text-xs">{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center sm:w-12 justify-center relative z-10 my-4 sm:my-0">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        item.current
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 scale-110'
                          : 'bg-white/5 border border-white/10'
                      }`}>
                        <Icon size={20} className={item.current ? 'text-white' : 'text-gray-500'} />
                      </div>
                    </div>

                    <div className="sm:hidden w-full">
                      <div className={`glass rounded-2xl p-5 ml-14 ${item.current ? 'border-indigo-500/20 bg-indigo-500/5' : 'hover:border-white/10'}`}>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-mono text-indigo-400">{item.period}</span>
                          {item.current && (
                            <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-mono">Current</span>
                          )}
                        </div>
                        <h3 className="text-base font-bold mb-2 text-white">{item.role}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed mb-3">{item.description}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {item.highlights.map((h) => (
                            <div key={h} className="flex items-center space-x-1.5">
                              <CheckCircle size={10} className="text-indigo-400 flex-shrink-0" />
                              <span className="text-gray-500 text-xs">{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <div className="glass rounded-3xl p-8 sm:p-10 border border-indigo-500/10 max-w-3xl mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/25">
                <Target size={24} className="text-white" />
              </div>
              <h3 className="text-white font-bold text-xl mb-2">{portfolioData.currentPosition.role}</h3>
              <p className="text-gray-400 text-sm mb-6">Current Focus Areas</p>
              <div className="flex flex-wrap justify-center gap-3">
                {portfolioData.currentPosition.focus.map((f) => (
                  <div key={f} className="px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl text-indigo-300 text-sm font-medium">
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Timeline;
